// doctor-frontend/src/pages/intern/components/ActivityCatalogGrid.js
// Full Guided Activity Catalogue surface inside Activity Launcher.
// No-regression catalogue reframing:
// - Preserves tenant-safe template launch, route/guideline behavior, role/context gating and pilot visibility overlay.
// - Keeps OPD / Primary Care as a Comprehensive Services handoff, never a guided ActivityInstance launch.
// - Uses the primary training-area colour metadata supplied by ActivityStartWizard for a professional catalogue.
// - Supports competencies, skills, learning domains, Bloom and Miller metadata when the backend template exposes them.
// - Keeps intern-facing catalogue clean while retaining pilot/config diagnostics for privileged administrative roles.
// - Keeps user-facing academic labels safe: display Core Practice Area instead of retired terminology.
// - Hides internal template codes from routine learner/reviewer cards while retaining them for privileged diagnostics.
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import GuidelineSearchBar from './GuidelineSearchBar';
import { activityApi } from '../../../services/activityApi';

function classNames(...arr){ return arr.filter(Boolean).join(' '); }

// --- tenant helpers (preserve current /t/:slug when navigating) ---
function safeJsonParse(v) {
  try { return JSON.parse(v); } catch { return null; }
}

function readTenantSlugFromStorage() {
  const candidates = [];
  try {
    const t1 = localStorage.getItem('tenantSlug');
    const t2 = localStorage.getItem('activeTenantSlug');
    const t3 = sessionStorage.getItem('tenantSlug');
    const t4 = sessionStorage.getItem('activeTenantSlug');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantSlug,
        obj?.tenantSlug,
        core?.tenant?.slug,
        obj?.tenant?.slug
      );
    });
  } catch {}

  const slug = candidates.map(s => String(s || '').trim()).find(Boolean);
  return slug || '';
}

function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch {}
}

function getTenantSlug() {
  // 1) URL path: /t/<slug>/...
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch { /* ignore */ }

  // 2) Storage fallback (critical after redirects that lose /t/:slug)
  return readTenantSlugFromStorage();
}

function tenantUrl(p) {
  if (!p) return '/';
  const slug = getTenantSlug();
  const path = String(p);
  if (!slug) return path; // no tenant context; use as-is
  if (!path.startsWith('/')) return `/t/${slug}/${path}`.replace(/\/{2,}/g,'/');
  if (path.startsWith(`/t/${slug}`)) return path;
  return `/t/${slug}${path}`.replace(/\/{2,}/g,'/');
}

// --- pilot/admin catalogue helpers ---
function isObjectId(v) {
  return typeof v === 'string' && /^[a-f\d]{24}$/i.test(v.trim());
}

function getTenantId() {
  try {
    const candidates = [];
    const keys = ['tenantId', 'activeTenantId'];
    keys.forEach((k) => {
      const l = localStorage.getItem(k);
      const s = sessionStorage.getItem(k);
      if (l) candidates.push(l);
      if (s) candidates.push(s);
    });

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenantObjectId,
        obj?.tenantObjectId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id
      );
    });

    return candidates.map(x => String(x || '').trim()).find(isObjectId) || '';
  } catch {
    return '';
  }
}

function getAuthToken() {
  const pick = (store) => {
    try {
      const preferred = ['cmfm_token', 'tenant_token', 'portal_token', 'jwt', 'token'];
      for (const key of preferred) {
        const hit = store.getItem(key);
        if (hit) return hit;
      }
      for (const key of Object.keys(store || {})) {
        if (/token/i.test(key)) {
          const hit = store.getItem(key);
          if (hit) return hit;
        }
      }
    } catch {}
    return null;
  };
  return pick(localStorage) || pick(sessionStorage);
}

const PROD_API_BASE_FALLBACK = 'https://aj-portal-backend-demo.onrender.com';

function getApiBase() {
  try {
    const raw = String(process.env.REACT_APP_API_BASE || '').trim().replace(/\/+$/, '');
    if (raw) return raw;
  } catch {}
  try {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
      return 'http://localhost:5000';
    }
  } catch {}
  return PROD_API_BASE_FALLBACK;
}

function apiUrl(path) {
  const p = String(path || '');
  if (/^https?:\/\//i.test(p)) return p;
  const base = getApiBase();
  return `${base}${p.startsWith('/') ? p : `/${p}`}`.replace(/([^:]\/)\/+/, '$1');
}

async function fetchJson(path) {
  const headers = {
    'Content-Type': 'application/json',
    'X-Requested-From': 'portal-tenant',
  };

  const tok = getAuthToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;

  const slug = getTenantSlug();
  if (slug) headers['X-Tenant-Slug'] = slug;

  const tenantId = getTenantId();
  if (tenantId) headers['X-Tenant-Id'] = tenantId;

  const r = await fetch(apiUrl(tenantUrl(path)), {
    method: 'GET',
    headers,
    credentials: 'include',
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(data?.message || data?.error || `Request failed: ${r.status}`);
    e.status = r.status;
    e.data = data;
    throw e;
  }
  return data;
}

function slugifyKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function buildCatalogueLookup(config) {
  const lookup = new Map();
  const items = Array.isArray(config?.catalogue) ? config.catalogue : [];

  items.forEach((item) => {
    const keys = [
      item?.key,
      item?.slug,
      item?.corePracticeAreaTitle,
      item?.practiceAreaTitle,
      item?.epaTitle, // legacy/internal field name kept for backward compatibility
      item?.title,
      item?.category ? `${item.category}-${item.displayOrder || ''}` : '',
    ]
      .map(slugifyKey)
      .filter(Boolean);

    keys.forEach((key) => {
      if (!lookup.has(key)) lookup.set(key, item);
    });
  });

  return lookup;
}

function findCatalogueItemForActivity(activity, lookup) {
  if (!activity || !lookup || !lookup.size) return null;

  const keys = [
    activity.pilotKey,
    ...(Array.isArray(activity.pilotKeys) ? activity.pilotKeys : []),
    activity.templateSlug,
    activity.slug,
    activity.code,
    activity.title,
    activity.id,
  ]
    .map(slugifyKey)
    .filter(Boolean);

  for (const key of keys) {
    if (lookup.has(key)) return lookup.get(key);
  }

  return null;
}

function shouldShowInPilot(activity, catalogueItem, config) {
  // Without an Admin visibility overlay, keep the server template visible.
  if (!config || !Array.isArray(config.catalogue) || !config.catalogue.length) return true;

  // Pilot mode controls visibility metadata; it does not replace the server catalogue.
  if (config.pilotMode === false) return true;

  // If an activity is not yet mapped into admin catalogue, keep it visible during migration.
  if (!catalogueItem) return true;

  const pilotStatus = String(catalogueItem.pilotStatus || '').trim().toLowerCase();

  if (catalogueItem.active === false) return false;
  if (catalogueItem.isPilotVisible === false) return false;
  if (pilotStatus === 'hold') return false;

  return true;
}

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function isOpdHandoffActivity(activity) {
  const keys = [
    activity?.pilotKey,
    ...(Array.isArray(activity?.pilotKeys) ? activity.pilotKeys : []),
    activity?.templateSlug,
    activity?.slug,
    activity?.code,
    activity?.id,
    activity?.title,
  ]
    .map(slugifyKey)
    .filter(Boolean);

  return keys.some((key) =>
    key === 'opd-primary-care' ||
    key === 'opd-common-ailments' ||
    key === 'diagnose-manage-common-ailments'
  );
}

function roleAliases(value) {
  const r = normalizeRoleToken(value);
  if (!r) return [];
  const out = new Set([r]);

  const map = {
    intern: ['intern'],
    jr: ['jr', 'junior resident'],
    'junior resident': ['jr', 'junior resident'],
    sr: ['sr', 'senior resident'],
    'senior resident': ['sr', 'senior resident'],
    faculty: ['faculty'],
    admin: ['admin'],
    deo: ['deo'],
    'medical officer': ['medical officer', 'mo'],
    mo: ['medical officer', 'mo'],
  };

  (map[r] || []).forEach((x) => out.add(x));
  return Array.from(out);
}

function roleCanStartActivity(activity, role) {
  const allowed = Array.isArray(activity?.roles) ? activity.roles : [];
  if (!allowed.length) return true;

  const userKeys = new Set(roleAliases(role));
  if (!userKeys.size) return false;

  return allowed.some((r) => roleAliases(r).some((alias) => userKeys.has(alias)));
}

function normalizeFilterToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function formatDisplayText(value) {
  const input = value == null ? '' : String(value);
  if (!input) return '';

  return input
    .replace(/Entrustable\s+Professional\s+Activities\s*\(\s*EPAs\s*\)/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/Entrustable\s+Professional\s+Activities/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPAs\b/g, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPA\b/g, CORE_PRACTICE_AREA_LABEL);
}

function displayList(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((v) => formatDisplayText(v).trim())
    .filter((v) => {
      const key = normalizeFilterToken(v);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function filterMatchesSelected(rawValue, selectedSet) {
  if (!selectedSet?.size) return true;
  const raw = normalizeFilterToken(rawValue);
  const display = normalizeFilterToken(formatDisplayText(rawValue));
  return selectedSet.has(raw) || selectedSet.has(display);
}

function mergeCatalogueIntoActivity(activity, catalogueItem) {
  if (!catalogueItem) return activity;

  return {
    ...activity,
    category: activity.groupKey ? activity.category : (catalogueItem.finalGroup || activity.category),
    pilotStatus: catalogueItem.pilotStatus || activity.pilotStatus,
    finalMode: catalogueItem.finalMode || activity.finalMode,
    requiresReviewer:
      typeof catalogueItem.requiresReviewer === 'boolean'
        ? catalogueItem.requiresReviewer
        : activity.requiresReviewer,
    _pilotConfig: catalogueItem,
  };
}

export default function ActivityCatalogGrid({
  citizenId = null,
  visitId = null,
  userRole = 'Intern',
  onStart = undefined,              // if not provided, we auto-start when templateId exists
  allowCategories = [],
  allowTags = [],
  initialFilter = { query: '', categories: [], tags: [] },
  hideUnavailable = false,
  activities = [],                  // backend ActivityTemplate catalogue; [] is authoritative
  disabled = false,
  pilotConfig = null,               // optional already-loaded Admin Activity Manager config
  pilotConfigLoading = false,
  pilotConfigError = '',
}) {
  const [query, setQuery] = useState(initialFilter.query || '');
  const [categories, setCategories] = useState(initialFilter.categories || []);
  const [tags, setTags] = useState(initialFilter.tags || []);
  const [localPilotConfig, setLocalPilotConfig] = useState(pilotConfig || null);
  const [localConfigLoading, setLocalConfigLoading] = useState(false);
  const [localConfigError, setLocalConfigError] = useState('');

  useEffect(() => {
    if (pilotConfig) {
      setLocalPilotConfig(pilotConfig);
      setLocalConfigError('');
    }
  }, [pilotConfig]);

  useEffect(() => {
    // If parent has already supplied config, do not duplicate the call.
    if (pilotConfig) return;

    let alive = true;
    (async () => {
      try {
        setLocalConfigLoading(true);
        const res = await fetchJson('/api/admin/activity-config');
        if (!alive) return;
        setLocalPilotConfig(res?.config || null);
        setLocalConfigError('');
      } catch (e) {
        if (!alive) return;
        setLocalPilotConfig(null);
        setLocalConfigError(e?.message || 'Pilot catalogue configuration could not be loaded.');
      } finally {
        if (alive) setLocalConfigLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [pilotConfig]);

  const effectivePilotConfig = pilotConfig || localPilotConfig;
  const effectiveConfigLoading = Boolean(pilotConfigLoading || localConfigLoading);
  const effectiveConfigError = pilotConfigError || localConfigError;
  const normalizedUserRole = normalizeRoleToken(userRole);
  const showConfigDiagnostics = [
    'admin',
    'administrator',
    'hod',
    'owner',
    'super admin',
  ].includes(normalizedUserRole);

  const displayCategories = useMemo(() => displayList(allowCategories), [allowCategories]);
  const displayTags = useMemo(() => displayList(allowTags), [allowTags]);

  const meetsContext = useCallback((activity) => {
    if (hideUnavailable) {
      if (activity.requiresCitizen && !citizenId) return false;
      if (activity.requiresVisit && !visitId) return false;
    }
    return true;
  }, [hideUnavailable, citizenId, visitId]);

  const sourceCatalog = useMemo(() => {
    const incoming = Array.isArray(activities) ? activities : [];
    const lookup = buildCatalogueLookup(effectivePilotConfig);

    return incoming
      .map((activity) => {
        const catalogueItem = findCatalogueItemForActivity(activity, lookup);
        if (!shouldShowInPilot(activity, catalogueItem, effectivePilotConfig)) return null;
        return mergeCatalogueIntoActivity(activity, catalogueItem);
      })
      .filter(Boolean);
  }, [activities, effectivePilotConfig]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    const catSet = new Set((categories || []).map(normalizeFilterToken).filter(Boolean));
    const tagSet = new Set((tags || []).map(normalizeFilterToken).filter(Boolean));

    const base = sourceCatalog.filter(a => {
      if (!meetsContext(a)) return false;
      if (!roleCanStartActivity(a, userRole)) return false;

      if (catSet.size && !filterMatchesSelected(a.category, catSet)) return false;

      if (tagSet.size) {
        const aTags = new Set(
          (a.tags || [])
            .flatMap((tag) => [tag, formatDisplayText(tag)])
            .map(normalizeFilterToken)
            .filter(Boolean)
        );
        for (const t of tagSet) if (!aTags.has(t)) return false;
      }

      if (q) {
        const hay = [
          a.title,
          formatDisplayText(a.title),
          a.code,
          formatDisplayText(a.code),
          a.description,
          formatDisplayText(a.description),
          a.pilotStatus,
          formatDisplayText(a.pilotStatus),
          a.finalMode,
          formatDisplayText(a.finalMode),
          ...(a.tags || []),
          ...displayList(a.tags || []),
          a.category,
          formatDisplayText(a.category)
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // Prioritize OPD flow first, then other Clinical, then alphabetically.
    const weight = (a) => {
      if (a.code === 'OPD_COMMON_AILMENTS') return 1000;
      if (a.category === 'Clinical') return 100;
      return 0;
    };
    return [...base].sort((a, b) => {
      const dw = weight(b) - weight(a);
      if (dw !== 0) return dw;
      const ta = (a.title || '').toLowerCase();
      const tb = (b.title || '').toLowerCase();
      return ta.localeCompare(tb);
    });
  }, [query, categories, tags, userRole, sourceCatalog, meetsContext]);

  useEffect(() => {
    const el = document.getElementById('intern-guideline-search');
    if (el) el.focus();
  }, []);

  async function autoStart(activity){
    if (disabled) return;

    // OPD care remains a Visit workflow. Never create a guided
    // ActivityInstance from an OPD catalogue card.
    if (isOpdHandoffActivity(activity)) {
      window.location.href = tenantUrl('/dashboard/operations');
      return;
    }

    if (activity?._pilotConfig && !shouldShowInPilot(activity, activity._pilotConfig, effectivePilotConfig)) {
      throw new Error('This activity is currently not visible in the pilot catalogue. Please check Admin → Activities.');
    }

    // Prefer parent callback if provided
    if (typeof onStart === 'function') {
      return onStart(activity, { citizenId, visitId, userRole });
    }

    // Fallbacks: templateId → start; else route → navigate; else guidelineHref → open
    const tplId = activity.templateId || activity.defaultTemplateId;
    if (tplId) {
      const payload = {
        templateId: tplId,
        centre: undefined,
        citizenId: citizenId || undefined,
        date: new Date().toISOString(),
      };
      const res = await activityApi.startActivity(payload);
      const id = res?.item?._id || res?._id || res?.id || res?.activityId || res?.runId;
      if (!id) throw new Error('Activity ID missing in response');
      window.location.href = tenantUrl(`/intern/activity/run/${encodeURIComponent(id)}`);
      return;
    }

    if (activity.route) {
      window.location.href = tenantUrl(activity.route);
      return;
    }

    if (activity.guidelineHref) {
      const href = activity.guidelineHref.startsWith('/')
        ? tenantUrl(activity.guidelineHref)
        : activity.guidelineHref;
      window.open(href, '_blank', 'noopener');
    }
  }

  return (
    <div className="w-full">
      {showConfigDiagnostics ? (
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {effectiveConfigLoading
              ? 'Pilot catalogue: Loading…'
              : effectivePilotConfig
                ? 'Pilot catalogue: Connected'
                : 'Pilot catalogue: Not connected'}
          </span>
          {effectivePilotConfig && (
            <span className="px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
              {effectivePilotConfig.pilotMode === false ? 'Legacy visibility' : 'Pilot visibility active'}
            </span>
          )}
          {effectivePilotConfig?.legacyMode && (
            <span className="px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">Legacy mode on</span>
          )}
          {effectiveConfigError && (
            <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              Config status: {effectiveConfigError}
            </span>
          )}
        </div>
      ) : null}

      <GuidelineSearchBar
        id="intern-guideline-search"
        query={query}
        setQuery={setQuery}
        categories={categories}
        setCategories={setCategories}
        tags={tags}
        setTags={setTags}
        allCategories={displayCategories}
        allTags={displayTags}
        onClear={() => { setQuery(''); setCategories([]); setTags([]); }}
      />

      <div
        className="mt-4 grid gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
      >
        {filtered.map(activity => {
          const isOpdHandoff = isOpdHandoffActivity(activity);
          const launchDisabled =
            disabled ||
            (activity.requiresCitizen && !citizenId) ||
            (activity.requiresVisit && !visitId) ||
            !roleCanStartActivity(activity, userRole);

          const hasGuideline = Boolean(activity.guidelineHref);
          const pilotStatus = String(activity.pilotStatus || '').trim();
          const finalMode = String(activity.finalMode || '').trim();
          const displayCode = showConfigDiagnostics ? formatDisplayText(activity.code) : '';
          const displayTitle = formatDisplayText(activity.title);
          const displayCategory = formatDisplayText(activity.category);
          const displayDescription = formatDisplayText(activity.description);
          const displayPilotStatus = formatDisplayText(pilotStatus);
          const displayFinalMode = formatDisplayText(finalMode).replace(/_/g, ' ');
          const allDisplayActivityTags = displayList(activity.tags || []);
          const displayActivityTags = allDisplayActivityTags.slice(0, 8);
          const hiddenTagCount = Math.max(0, allDisplayActivityTags.length - displayActivityTags.length);
          const displayCompetencies = displayList(activity.competencies || []).slice(0, 3);
          const displaySkills = displayList(activity.skills || []).slice(0, 4);
          const displayDomains = displayList(activity.learningDomains || []).slice(0, 4);
          const displayBloom = formatDisplayText(activity.bloomLevel || '');
          const displayMiller = formatDisplayText(activity.millerLevel || '');
          const accent = String(activity.groupAccent || '#475569');
          const soft = String(activity.groupSoft || '#F1F5F9');

          return (
            <div
              key={activity.id || activity.code || activity.title}
              className={classNames(
                'rounded-2xl border p-4 shadow-sm overflow-hidden',
                'bg-white dark:bg-neutral-900 dark:border-neutral-800'
              )}
              style={{
                borderTop: `5px solid ${accent}`,
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {displayCode ? (
                    <div className="text-xs opacity-60 break-all">Template key: {displayCode}</div>
                  ) : null}
                  <h3 className={`text-lg font-semibold leading-snug ${displayCode ? 'mt-1' : ''}`}>{displayTitle}</h3>
                </div>
                <span
                  className="text-xs px-2 py-1 rounded-full whitespace-nowrap font-semibold"
                  style={{ backgroundColor: soft, color: accent }}
                >
                  {displayCategory}
                </span>
              </div>

              <p className="mt-2 text-sm opacity-80 leading-relaxed">
                {displayDescription}
              </p>

              {(displayCompetencies.length || displaySkills.length || displayDomains.length || displayBloom || displayMiller) ? (
                <div
                  className="mt-3 rounded-xl border px-3 py-2"
                  style={{ borderColor: `${accent}55`, backgroundColor: `${soft}` }}
                >
                  {displayCompetencies.length ? (
                    <div className="text-xs leading-relaxed">
                      <span className="font-semibold">Competencies:</span>{' '}
                      {displayCompetencies.join(' • ')}
                    </div>
                  ) : null}

                  {displaySkills.length ? (
                    <div className="text-xs leading-relaxed mt-1">
                      <span className="font-semibold">Skills:</span>{' '}
                      {displaySkills.join(' • ')}
                    </div>
                  ) : null}

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {displayDomains.map((domain) => (
                      <span
                        key={`domain-${domain}`}
                        className="text-[11px] px-2 py-1 rounded-full bg-white/80 dark:bg-neutral-800"
                      >
                        {domain}
                      </span>
                    ))}
                    {displayBloom ? (
                      <span className="text-[11px] px-2 py-1 rounded-full bg-white/80 dark:bg-neutral-800">
                        Bloom: {displayBloom}
                      </span>
                    ) : null}
                    {displayMiller ? (
                      <span className="text-[11px] px-2 py-1 rounded-full bg-white/80 dark:bg-neutral-800">
                        Miller: {displayMiller}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2">
                {displayActivityTags.map(t => (
                  <span
                    key={t}
                    className="text-xs px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800"
                  >
                    #{t}
                  </span>
                ))}
                {hiddenTagCount ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
                    +{hiddenTagCount} more
                  </span>
                ) : null}
                {showConfigDiagnostics && pilotStatus && (
                  <span className="text-xs px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
                    Pilot: {displayPilotStatus}
                  </span>
                )}
                {showConfigDiagnostics && finalMode && (
                  <span className="text-xs px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
                    Mode: {displayFinalMode}
                  </span>
                )}
              </div>

              <div className="mt-3 text-xs opacity-70">
                {activity.estimatedMinutes ? `~${activity.estimatedMinutes} min` : '—'} · {activity.difficulty || '—'}
                {activity.requiresCitizen ? ' · Needs citizen' : ''}
                {activity.requiresVisit ? ' · Needs visit' : ''}
                {activity.requiresReviewer === true ? ' · Reviewer required' : ''}
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                <button
                  type="button"
                  disabled={launchDisabled}
                  onClick={() => autoStart(activity)}
                  className={classNames(
                    'px-3 py-2 rounded-lg text-sm font-semibold transition',
                    launchDisabled
                      ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed dark:bg-neutral-800 dark:text-neutral-600'
                      : 'text-white'
                  )}
                  style={launchDisabled ? undefined : { backgroundColor: accent }}
                >
                  {isOpdHandoff ? 'Open Comprehensive Services' : 'Launch Activity'}
                </button>

                {hasGuideline && (
                  <a
                    href={activity.guidelineHref.startsWith('/') ? tenantUrl(activity.guidelineHref) : activity.guidelineHref}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-lg text-sm font-medium border"
                  >
                    Open Guideline
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!filtered.length && (
        <div className="mt-10 text-center opacity-70">
          No guided activities match your current search or filters.
        </div>
      )}
    </div>
  );
}
